import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { LanguageService } from 'services';
import * as i18nIsoCountries from 'i18n-iso-countries';
import * as _ from 'lodash';

import { hasCyrillicCharactersValidator } from '../../../../helpers/form-validators';
import { glnPattern } from '../../../../../../order-management/retailer-platform/manage-stores/shared';
import { Branch, OrganizationModel } from 'models';

@Component({
    selector: 'fc-store-info-form',
    templateUrl: './store-info-form.component.html',
    styleUrls: ['./store-info-form.component.less', '../../../../../../shared/styles/components/fc-guestpanel.less'],
    standalone: false
})
export class StoreInfoFormComponent implements OnInit {
    @Output() storeFormSubmitted = new EventEmitter<FormGroup>();
    @Output() backClicked = new EventEmitter<void>();

    public storeForm: FormGroup;
    public countries: Observable<{ value: string; label: string }[]>;
    public languages: { value: string; viewValue: string }[];
    public name: string;
    public termsPdfLink: Observable<string>;
    public privacyLink: Observable<string>;
    public branch: Branch;

    private countrySearchQuery: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private readonly topCountries: i18nIsoCountries.LocalizedCountryNames;

    constructor(
        private languageService: LanguageService,
        formBuilder: FormBuilder,
        private organizationModel: OrganizationModel
    ) {
        this.topCountries = {
            BE: 'Belgium',
            DK: 'Denmark',
            DE: 'Germany',
            NL: 'Netherlands',
            GB: 'United Kingdom'
        };
        this.storeForm = formBuilder.group({
            name: new FormControl(this.name, [Validators.required, hasCyrillicCharactersValidator()]),
            phone: new FormControl('', [Validators.required, Validators.pattern('[- +()0-9]+')]),
            address1: new FormControl('', [Validators.required, hasCyrillicCharactersValidator()]),
            zip: new FormControl('', [Validators.required]),
            city: new FormControl('', [Validators.required, hasCyrillicCharactersValidator()]),
            country: new FormControl({ value: 'de', label: 'Germany' }, [Validators.required]),
            glnForm: formBuilder.group({
                useFcGln: [false, Validators.required],
                gln: [
                    '',
                    [Validators.required, Validators.pattern(glnPattern)],
                    (control) => this.existingGlnValidator(control)
                ],
                buyerGln: ['', Validators.pattern(glnPattern)],
                billingGln: ['', Validators.pattern(glnPattern)]
            })
        });
        this.glnForm.get('useFcGln').valueChanges.subscribe((useFcGln) => {
            const glnControl = this.glnForm.get('gln');
            const buyerGlnControl = this.glnForm.get('buyerGln');
            const billingGlnControl = this.glnForm.get('billingGln');

            if (useFcGln) {
                buyerGlnControl.setValue('');
                billingGlnControl.setValue('');
                glnControl.setValue('');
                glnControl.disable();
                buyerGlnControl.disable();
                billingGlnControl.disable();
            } else {
                glnControl.enable();
                buyerGlnControl.enable();
                billingGlnControl.enable();
            }
        });
        this.branch = { address: {} } as Branch;
    }

    ngOnInit(): void {
        this.countries = combineLatest([this.languageService.currentLanguage, this.countrySearchQuery]).pipe(
            distinctUntilChanged(),
            map(([language, searchQuery]) => {
                const allCountries = i18nIsoCountries.getNames(language);
                const countries = { ...this.topCountries, ...allCountries };
                const countriesPairs = _.toPairs(countries);
                return _.map(
                    _.filter(countries, (c) => new RegExp(`^${searchQuery}`, 'i').test(c)),
                    (name: string) => {
                        const countryCode = countriesPairs.find((pair) => pair[1] === name);
                        return {
                            value: _.lowerCase(countryCode[0]),
                            label: name
                        };
                    }
                );
            })
        );
        const getPdfLanguage = (language: string) => (language === 'de' ? 'de' : 'en');
        this.termsPdfLink = this.languageService.currentLanguage.pipe(
            map((language) => `/assets/pdfs/terms-and-conditions-${getPdfLanguage(language)}.pdf`)
        );
        this.privacyLink = this.languageService.currentLanguage.pipe(
            map(
                (language) =>
                    `https://fashion.cloud/${language === 'de' ? 'de/datenschutz/general' : 'privacy-policy/general'}`
            )
        );
    }

    public getErrorTranslationKey(form: FormGroup, inputName: string, fallbackKey: string) {
        const isCyrillicError = form.controls[inputName].errors && form.controls[inputName].errors.cyrillic;

        return isCyrillicError ? 'SIGNUP.FORM.VALIDATION.HAS_CYRILLIC' : fallbackKey;
    }

    public submitStore(storeForm: FormGroup) {
        this.storeFormSubmitted.emit(storeForm);
    }

    public onSearchQueryChange(name: string, field: 'country') {
        this.countrySearchQuery.next(name);
    }

    public isFieldInvalid(form: FormGroup, field: string): boolean {
        return form.controls[field].invalid && (form.controls[field].dirty || form.controls[field].touched);
    }

    public updateTermsAccepted(value: boolean): void {
        this.storeForm.patchValue({ termsAccepted: value });
    }

    get glnForm() {
        return this.storeForm.get('glnForm');
    }

    private async existingGlnValidator(glnFormControl: AbstractControl) {
        const gln = _.toString(glnFormControl.value);
        // Only run async validation when value has changed
        if (gln === this.branch.address.gln) {
            return Promise.resolve(null);
        }

        try {
            const branches = await this.organizationModel.getBranchesByGln(gln);
            return branches.length === 0 ? null : { existingGln: { value: glnFormControl.value } };
        } catch (error) {
            this.onServerError(error);
        }
    }

    private onServerError(error) {
        this.storeForm.disable();
        this.storeForm.setErrors({ serverError: error });
    }
}
